import { useEffect, useState } from "react";
import { Link, generatePath, useParams } from "react-router-dom";
import EntityTypes from "../../models/entityTypes";
import Project from "../../models/project";
import { api } from "../../store/api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectLabels } from "../../store/labelsSlice";
import { PartnerDetailsRoute } from "../partners/partnerDetailsScreen";
import RecordDetailsCard from "../../components/records/recordDetailsCard";
import KnowledgeMapTypes from "../../models/knowledgeMapTypes";
import { setTitle } from "../../util/useDocumentTitle";
import { usePermissions } from "../../util/usePermissions";
import { ProjectDetailsRoute } from "./projectDetailsScreen";
import FileBlob from "../../models/fileBlob";
import { AsyncOperation } from "../../models/asyncOperation";
import { useDispatch } from "react-redux";
import { monitorOperation } from "../../realtime/middleware";
import { selectSession } from "../../store/sessionSlice";


export const ProjectDeleteRoute = "/projects/:id/delete";

interface ProjectDeleteRouteParams {
  id?: string;
}

export default function ProjectDeleteScreen() {
  //const dispatch = useAppDispatch();
  const dispatch = useDispatch();
  const labels = useAppSelector(selectLabels);
  const asyncOperations = useAppSelector(selectSession).operations;

  const [isLoading, setIsLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [record, setRecord] = useState<Project>();
  const [error, setError] = useState<String>();
  const [inProgress, setInProgress] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>("Beginning delete...");
  const [operations, setOperations] = useState<AsyncOperation[]>();

  const { hasPermission } = usePermissions();
  
  //const asyncOperations = operations?.find((o) => o.context === operation?.context);

  const params = useParams<keyof ProjectDeleteRouteParams>();
  useEffect(() => {
    // Load the attribute and set the form with the current values
    setIsLoading(true);
    api.projects.get(parseInt(params.id!))
      .then((record) => {
        setRecord(record);
        setTitle([`Delete ${labels.project.singular}`, record.name!]);
        setIsLoading(false);
      })
      .catch((reason) => {
        console.error(reason);
        setError("Unable to load " + labels.project.singular + ` #${params.id}`);
      });
  }, [labels.project.singular, params.id]);

  useEffect(() => {
    if (operations) {
      operations.forEach((operation) => dispatch(monitorOperation(operation.context)));      
    }
  }, [dispatch, operations]);

  const onConfirm = () => {
    if (record?.id) {
      setConfirmed(true);
      setInProgress(true);
      // API delete
      api.project(record.id).delete()
        .then((response) => {
          if (response) {
            setOperations(response);
          }
          else {
            setError('Unable to track progress of delete operation');
          }
        })
        .catch((reason) => {
          console.error(reason);
          setError("Error while processing: Delete " + labels.project.singular + ` #${params.id}`);
        });

    }
  };

  return (<div>
    <div className="d-flex align-items-center mb-3">
      <div>
        <h1 className="page-header mb-0">Export {labels.project.singular}</h1>
        <ul className="breadcrumb">
          <li className="breadcrumb-item"><Link to={generatePath(PartnerDetailsRoute, { id: `${record?.partner?.id}` })}>{record?.partner?.name}</Link></li>
          <li className="breadcrumb-item"><Link to={generatePath(ProjectDetailsRoute, { id: `${record?.id}` })}>{record?.name}</Link></li>
          <li className="breadcrumb-item active">{labels.project.singular} Delete</li>
        </ul>
      </div>
    </div>
    {error && <div className="alert alert-danger"><strong>Error!</strong> {error}</div>}
    <div className="row">
      <div className="col-md-4">
        {record && <RecordDetailsCard
          record={record}
          type={EntityTypes.Project}
          // editRoute={ProjectEditRoute}
          // footer={<>
          //   <RequiresPermission permission={PermissionTypes.ProjectEdit}>
          //     <Link to={generatePath(ProjectResourcesRoute, { projectId: `${record?.id}` })} className="ms-auto text-decoration-none text-gray-500"><i className="fa fa-edit fa-lg me-2 ms-n2"></i> Add</Link>
          //   </RequiresPermission>
          // </>}
          displayAttributes={[
            { label: labels.partner.singular, callback: (record: Project) => <Link to={generatePath(PartnerDetailsRoute, { id: `${record?.partnerId}` })}>{record?.partner?.name}</Link>, },
            { label: 'Knowledge Map Type', callback: (record: Project) => <>{labels.knowledgeMapTypes[record.knowledgeMapType ?? KnowledgeMapTypes.Undefined]}</>, },
            { label: labels.unitevaluation.plural, callback: (record: Project) => <>{record.unitEvaluations === true ? 'Yes' : 'No'}</>, },
          ]}
        />}
      </div>
      <div className="col-md-8">
        <div className="card border-0 mb-4">
          <div className="card-body">
            {record && confirmed === false && <>
              <h3>Are you sure?</h3>
              <p>
                Deleting this project is permenant! It will remove all of the {labels.review.plural}, {labels.unitevaluation.plural}, Grades/Units, and {labels.reviewer.plural} for this {labels.project.singular}.
              </p>
              <p>
                An alternative is to mark the project as Inactive -- keeping the data but hiding it from users and reporting.
              </p>
              <button className="btn btn-danger" type="button" onClick={() => onConfirm()}>Yes, I am sure: Delete the {record.name} {labels.project.singular}.</button>
            </>}
            {isLoading && <p><i className="fa-solid fa-spin fa-spinner"></i> Loading...</p>}

            {operations && <> 
              {operations.map((operation) => {
                const asyncOperation = asyncOperations?.find((o) => o.context === operation.context);
                if (asyncOperation) {
                  return <div key={asyncOperation.context}>
                    <h3 title={asyncOperation.context}>{operation.title}</h3>
                    <p>{asyncOperation !== undefined && asyncOperation.complete !== true && <i className="fa-solid fa-spin fa-spinner"></i>} {asyncOperation.message}</p>
                  </div>;
                }
                return null;
              })}
            </>}
            
          </div>
        </div>
      </div>
    </div>
  </div>);
}
